import { axios } from 'api'

export interface Product {
  id: string
  itemName: string
  stock: number
  basePrice: number | null
  itemType: string
  description: string | null
  weight: number | null
  width: number | null
  length: number | null
  height: number | null
  imageUrl: string | null
  canBeAssembled: boolean | null
  canBeHinged: boolean | null
  componentOfKitPackageItem: boolean | null
  isDiscountable: boolean | null
  isActive: boolean | null
  collectionId: string | null
  collection?: { id: string; name: string } | null
  color?: { id: string; name: string; productKeyword: string } | null
  category?: { id: string; name: string; slug: string } | null
  productType?: { id: string; name: string; slug: string } | null
  createdAt: string
  updatedAt: string
}

export interface GetProductsParams {
  page?: number
  pageSize?: number
  keyword?: string
  includeInactive?: boolean
  includeKitComponents?: boolean
  itemType?: string | string[]
  sortField?: string
  sortDirection?: 'asc' | 'desc'
}

export interface GetProductsResponse {
  totalItems: number
  totalPages: number
  currentPage: number
  pageSize: number
  products: Product[]
  itemTypes: string[]
  widthTypes: { width: string; count: number }[]
  heightTypes: { height: string; count: number }[]
}

export interface SyncProductsResponse {
  message: string
  updatedCount: number
  createdCount?: number
  productLookupDataSync?: {
    collections: {
      created: number
      updated: number
      deactivated: number
      unchanged: number
    }
    colors: {
      created: number
      updated: number
      deactivated: number
      unchanged: number
      skipped?: number
    }
  }
  updates: Array<{
    itemName: string
    changeType: string
    changes: Record<string, { from: any; to: any }>
  }>
}

export type ProductSyncJobStatus = 'running' | 'completed' | 'failed'

export interface ProductSyncJobResponse {
  id: string
  type: string
  status: ProductSyncJobStatus
  source?: string | null
  payload?: Record<string, any> | null
  startedAt: string | null
  updatedAt: string
  completedAt?: string | null
  result?: SyncProductsResponse
  error?: string | null
}

export const getProducts = async (
  params: GetProductsParams = {}
): Promise<GetProductsResponse> => {
  const response = await axios.get('/products', {
    params,
    withCredentials: true
  })

  if (response.status !== 200) {
    throw new Error(response.data?.error || 'Failed to fetch products')
  }

  return response.data
}

export const getProductCategories = async (): Promise<string[]> => {
  const response = await axios.get('/products/categories', {
    withCredentials: true
  })
  if (response.status !== 200) return []
  return response.data.categories ?? []
}

export const syncProducts = async (): Promise<ProductSyncJobResponse> => {
  const response = await axios.post(
    '/products/sync-products',
    {},
    {
      withCredentials: true
    }
  )

  if (response.status !== 200 && response.status !== 202) {
    throw new Error(response.data?.error || 'Failed to sync products')
  }

  return response.data
}

export const getProductSyncJob = async (
  jobId: string
): Promise<ProductSyncJobResponse> => {
  const response = await axios.get(`/products/sync-products/${jobId}`, {
    withCredentials: true
  })

  if (response.status !== 200 && response.status !== 202) {
    throw new Error(response.data?.error || 'Failed to fetch sync status')
  }

  return response.data
}

export const updateProductStatus = async (
  id: string,
  body: { isActive?: boolean; isDiscountable?: boolean }
): Promise<Product> => {
  const response = await axios.patch(`/products/${id}/status`, body, {
    withCredentials: true
  })

  if (response.status !== 200) {
    throw new Error(response.data?.error || 'Failed to update product')
  }

  return response.data.product
}
